import { TAU } from '../core/geometry'

/**
 * The mark for a claim an objection below it stood against, in reasoning: a
 * notch cut into its disc.
 *
 * The record already said it everywhere but the map — the card, the report's
 * "What didn't survive", the markdown and the feed print "an objection below it
 * stood, in reasoning" (`OBJECTION_STOOD_BELOW`) — while the canvas drew the
 * claim exactly as it drew one nothing had touched, and put its whole mark on
 * the objection: a crossed disc drained to grey and closed with a bar.
 *
 * Not a strike. The design report asked for the parent's label struck through,
 * and a strike is already taken, twice over, by the one statement on this
 * canvas that comes from outside the graph: two chords across a disc are
 * `refuted`, which only the citation gate and a recorded result may say (D140,
 * invariant 2). An argument the model had with itself, drawn as that, would
 * say the opposite of what the gate allows. A notch says what happened and no
 * more: the claim still stands, whole but for a piece the graph's own argument
 * took out of it. It is the disc's own outline, so it needs no new hue and no
 * new ink — it wears the node's channel like every glyph here, reads against
 * the field at the disc's own contrast, and holds still under reduced motion.
 *
 * Where it is cut: on the inward half of the disc, three eighths of a turn from
 * its outward spoke. Captions stand outward on the spoke and a two-line one
 * lies across the disc's outer half — its halo would swallow a cut there, as it
 * swallowed the broken rim's opening (`rim.ts`) — and a node's own branches fan
 * out that way too. The inward half is the one no caption goes first. Not dead
 * inward either: the strand the node was derived along arrives there, and would
 * show through the cut as though it had bitten in. On the spoke, like every
 * radial mark, so it holds still as siblings land.
 */

/** Where the notch is cut, from the node's outward spoke: three eighths of a turn. */
export const NOTCH_AT = (3 * Math.PI) / 4
/** Half the notch's mouth at the rim, in radians: about sixty-five degrees open,
 *  a bite a four-pixel disc still shows, and short of a wedge that reads as a pie. */
export const NOTCH_HALF = 0.55
/** How far in the notch reaches, as a share of the radius it is cut into. */
export const NOTCH_DEPTH = 0.6

/**
 * The outline of a disc — or a ring's centre line — of radius `r` with the notch
 * cut in, for a node whose outward spoke points at `spoke`. The caller fills it
 * (a disc), or strokes it (a hollow ring, or the crisp contour that follows a
 * disc's edge).
 */
export function traceNotched(
  ctx: Pick<CanvasRenderingContext2D, 'beginPath' | 'arc' | 'lineTo' | 'closePath'>,
  x: number,
  y: number,
  r: number,
  spoke: number,
) {
  const at = spoke + NOTCH_AT
  const inner = r * (1 - NOTCH_DEPTH)
  ctx.beginPath()
  ctx.arc(x, y, r, at + NOTCH_HALF, at - NOTCH_HALF + TAU)
  ctx.lineTo(x + Math.cos(at) * inner, y + Math.sin(at) * inner)
  ctx.closePath()
}
